import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ConfirmModal from "../ConfirmModal";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import CompRecordEditModal, { employeeFromRecord } from "./CompRecordEditModal";
import { employeeName } from "./WhtPickers";
import { GlPreviewModal, GlStatusCell } from "./glUi";
import { formatCentavos } from "../../utils/whtMoney";

// Saved compensation records for the client's year (the page's year select),
// filtered by month. Edit (PUT) and delete both go through useGuardedMutation.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CompRecordsList = ({ client, year, refreshKey = 0, onOpenPostingAccounts }) => {
  const [month, setMonth] = useState(0); // 0 = whole year
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [warningsById, setWarningsById] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [glModal, setGlModal] = useState(null); // { ids, month? }
  const clientId = client?._id;

  const load = useCallback(async () => {
    if (!clientId || !year) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withholdingTaxAPI.listCompRecords(clientId, { year, month: month || undefined });
      setRecords(res?.data || []);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || "Couldn't load records");
    } finally {
      setLoading(false);
    }
  }, [clientId, year, month]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doDelete = async () => {
    try {
      await withholdingTaxAPI.deleteCompRecord(clientId, pendingDelete._id);
      setPendingDelete(null);
      notify.success("Deleted", "The compensation record was deleted.");
      await load();
    } catch (e) {
      notify.error("Couldn't delete", e.message || "Try again.");
    }
  };
  const { mutate: runDelete, isPending: deleting } = useGuardedMutation(doDelete);

  const totals = records.reduce(
    (acc, r) => ({ total: acc.total + (r.totalCompCentavos || 0), tax: acc.tax + (r.taxWithheldCentavos || 0) }),
    { total: 0, tax: 0 }
  );
  const unposted = records.filter((r) => !r.isPostedToGL && r.glStatus !== "posting");
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = unposted.length > 0 && unposted.every((r) => selected.has(r._id));
  const nameOf = (r) => employeeName(employeeFromRecord(r) || {});

  return (
    <section aria-labelledby="comp-records-heading" className="mt-8">
      <div className="flex items-end justify-between gap-4 mb-3">
        <div>
          <h3 id="comp-records-heading" className="text-lg font-medium text-ink">Saved compensation records</h3>
          <p className="text-sm text-ink-body">{year}</p>
        </div>
        <label className="text-sm text-ink-body">
          <span className="mr-2">Month</span>
          <select aria-label="Month filter" value={month} onChange={(e) => setMonth(Number(e.target.value))}
            className="border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
            <option value={0}>Whole year</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="text-sm text-danger-500 mb-2">{error}</p>}

      <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
        <Button variant="outline" size="sm" disabled={!selected.size} onClick={() => setGlModal({ ids: [...selected] })}>
          Preview & post ({selected.size})
        </Button>
        <Button variant="outline" size="sm" disabled={!month || !unposted.length}
          title={month ? undefined : "Pick a month first"}
          onClick={() => setGlModal({ ids: unposted.map((r) => r._id), month: { year, month, kind: "COMP" } })}>
          Post all unposted (month)
        </Button>
      </div>

      <div className="overflow-x-auto border border-ink-line rounded-lg">
        <table className="min-w-full divide-y divide-ink-line">
          <thead className="bg-ink-canvas">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" aria-label="Select all unposted" checked={allSelected} disabled={!unposted.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(unposted.map((r) => r._id)) : new Set())} className="h-4 w-4 accent-primary-500" />
              </th>
              {["Month", "Employee", "Description", "Total comp (14)", "Tax withheld (25)", "", "GL", "Actions"].map((h, i) => (
                <th key={h || `col-${i}`} className={`px-3 py-3 text-xs font-medium text-ink-body uppercase tracking-wider ${i === 3 || i === 4 ? "text-right" : "text-left"}`}>
                  {h || <span className="sr-only">Warnings</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-line">
            {loading && records.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-ink-body">Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-ink-body">No records for this period.</td></tr>
            )}
            {records.map((r) => {
              const warnings = [...(warningsById[r._id] || [])];
              if (!r.employeeTin) warnings.push({ code: "EMPLOYEE_NO_TIN", message: "Employee has no TIN — required on the alphalist" });
              return (
                <tr key={r._id} data-record-id={r._id}>
                  <td className="px-3 py-2">
                    <input type="checkbox" aria-label={`Select ${nameOf(r)} ${MONTHS[r.month - 1]}`} checked={selected.has(r._id)}
                      disabled={r.isPostedToGL || r.glStatus === "posting"} onChange={() => toggle(r._id)} className="h-4 w-4 accent-primary-500" />
                  </td>
                  <td className="px-3 py-2 text-sm text-ink">{MONTHS[r.month - 1]}</td>
                  <td className="px-3 py-2 text-sm text-ink">
                    {nameOf(r)}
                    {r.employeeIsMwe && <span className="ml-2 text-xs px-2 py-0.5 rounded-md bg-primary-50 text-primary-700">MWE</span>}
                    {r.employeeTin && <span className="block text-xs text-ink-body tabular-nums">{r.employeeTin}</span>}
                  </td>
                  <td className="px-3 py-2 text-sm text-ink">{r.description || "—"}</td>
                  <td className="px-3 py-2 text-sm text-ink text-right tabular-nums">₱{formatCentavos(r.totalCompCentavos)}</td>
                  <td className="px-3 py-2 text-sm text-ink text-right tabular-nums">₱{formatCentavos(r.taxWithheldCentavos)}</td>
                  <td className="px-3 py-2">
                    {warnings.length > 0 && (
                      <span title={warnings.map((w) => w.message).join("\n")} aria-label={`Warnings: ${warnings.map((w) => w.message).join("; ")}`}>
                        <AlertTriangle className="w-4 h-4 text-warning-500" />
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2"><GlStatusCell record={r} /></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(r)} aria-label={`Edit ${nameOf(r)} ${MONTHS[r.month - 1]}`} />
                      <Button variant="dangerGhost" size="sm" icon={Trash2} onClick={() => setPendingDelete(r)} aria-label={`Delete ${nameOf(r)} ${MONTHS[r.month - 1]}`} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {records.length > 0 && (
            <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
              <tr>
                <td colSpan={4} className="px-3 py-3 text-sm font-medium text-ink-body">{records.length} record{records.length !== 1 ? "s" : ""}</td>
                <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.total)}</td>
                <td className="px-3 py-3 text-sm font-medium text-ink text-right tabular-nums">₱{formatCentavos(totals.tax)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editing && (
        <CompRecordEditModal
          client={client}
          record={editing}
          onOpenPostingAccounts={onOpenPostingAccounts && (() => { setEditing(null); onOpenPostingAccounts(); })}
          onClose={() => setEditing(null)}
          onSaved={async (updated, warnings) => {
            setEditing(null);
            setWarningsById((prev) => ({ ...prev, [updated._id]: warnings }));
            await load();
          }}
        />
      )}

      {glModal && (
        <GlPreviewModal
          client={client}
          ids={glModal.ids}
          month={glModal.month}
          onClose={() => setGlModal(null)}
          onPosted={load}
          onOpenPostingAccounts={onOpenPostingAccounts && (() => { setGlModal(null); onOpenPostingAccounts(); })}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(pendingDelete)}
        title="Delete this compensation record?"
        message={pendingDelete
          ? `${nameOf(pendingDelete)} · ${MONTHS[pendingDelete.month - 1]} ${pendingDelete.year} · ₱${formatCentavos(pendingDelete.totalCompCentavos)}. ${pendingDelete.isPostedToGL ? "This record is posted — this will post a reversal. " : ""}This can't be undone.`
          : ""}
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={() => runDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
};

export default CompRecordsList;
